import React, { useState, useEffect } from 'react';
import './App.css';

// Logo Component
const Logo = () => (
  <svg viewBox="0 0 200 200" className="logo-svg">
    <defs>
      <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#d4af37" />
        <stop offset="50%" stopColor="#c9a227" />
        <stop offset="100%" stopColor="#b8960f" />
      </linearGradient>
    </defs>
    <circle cx="100" cy="100" r="90" fill="none" stroke="url(#goldGradient)" strokeWidth="3" />
    <circle cx="100" cy="100" r="80" fill="none" stroke="#1a365d" strokeWidth="2" />
    {/* Graduation Cap */}
    <path d="M100 50 L140 70 L100 90 L60 70 Z" fill="#1a365d" />
    <rect x="95" y="90" width="10" height="20" fill="#1a365d" />
    <path d="M70 70 L70 95 Q100 115 130 95 L130 70" fill="none" stroke="#1a365d" strokeWidth="3" />
    {/* Book */}
    <path d="M60 100 Q100 90 140 100 L140 140 Q100 130 60 140 Z" fill="none" stroke="url(#goldGradient)" strokeWidth="2" />
    <line x1="100" y1="95" x2="100" y2="135" stroke="url(#goldGradient)" strokeWidth="2" />
    {/* Diploma */}
    <ellipse cx="130" cy="145" rx="15" ry="8" fill="url(#goldGradient)" />
    <rect x="115" y="140" width="30" height="5" fill="url(#goldGradient)" />
  </svg>
);

// Navbar Component
const Navbar = ({ activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#accueil', label: 'Accueil' },
    { href: '#services', label: 'Services' },
    { href: '#technologies', label: 'Technologies' },
    { href: '#methodologies', label: 'Méthodologies' },
    { href: '#pourquoi', label: 'Pourquoi Nous' },
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <a href="#accueil" className="nav-logo">
          <Logo />
          <span>PFE & Master Coach</span>
        </a>
        <div className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`nav-link ${activeSection === link.href.slice(1) ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
        </div>
        <button
          className="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
      </div>
    </nav>
  );
};

// Hero Section
const Hero = () => (
  <section id="accueil" className="hero">
    <div className="hero-bg-shapes">
      <div className="shape shape-1"></div>
      <div className="shape shape-2"></div>
      <div className="shape shape-3"></div>
    </div>
    <div className="hero-content">
      <div className="hero-text">
        <div className="hero-badge animate-fade-in">
          <i className="fas fa-graduation-cap"></i>
          <span>Accompagnement Académique Premium</span>
        </div>
        <h1 className="animate-slide-up">
          <span className="highlight">PFE</span> & <span className="highlight">Master</span> Coach
        </h1>
        <p className="hero-subtitle animate-slide-up-delay">
          Votre partenaire de confiance pour réussir vos projets de fin d'études 
          et mémoires de Master avec excellence
        </p>
        <div className="hero-stats animate-fade-in-delay">
          <div className="stat">
            <span className="stat-number">500+</span>
            <span className="stat-label">Projets Réussis</span>
          </div>
          <div className="stat">
            <span className="stat-number">98%</span>
            <span className="stat-label">Satisfaction</span>
          </div>
          <div className="stat">
            <span className="stat-number">50+</span>
            <span className="stat-label">Spécialités</span>
          </div>
        </div>
        <div className="hero-cta animate-slide-up-delay-2">
          <a href="#contact" className="btn btn-primary">
            <i className="fas fa-rocket"></i>
            Commencer Maintenant
          </a>
          <a href="#services" className="btn btn-secondary">
            <i className="fas fa-eye"></i>
            Découvrir Nos Services
          </a>
        </div>
      </div>
      <div className="hero-visual animate-float">
        <div className="hero-image-container">
          <div className="hero-logo-large">
            <Logo />
          </div>
          <div className="floating-icons">
            <div className="floating-icon icon-1"><i className="fab fa-react"></i></div>
            <div className="floating-icon icon-2"><i className="fab fa-python"></i></div>
            <div className="floating-icon icon-3"><i className="fab fa-java"></i></div>
            <div className="floating-icon icon-4"><i className="fas fa-database"></i></div>
            <div className="floating-icon icon-5"><i className="fab fa-docker"></i></div>
            <div className="floating-icon icon-6"><i className="fas fa-brain"></i></div>
          </div>
        </div>
      </div>
    </div>
    <div className="scroll-indicator">
      <div className="mouse">
        <div className="wheel"></div>
      </div>
      <span>Défiler vers le bas</span>
    </div>
  </section>
);

// Services Section
const Services = () => {
  const services = [
    {
      icon: 'fa-laptop-code',
      title: 'Projets PFE Complets',
      description: 'Accompagnement de A à Z pour vos projets de fin d\'études : analyse, conception, développement et déploiement.',
      features: ['Analyse des besoins', 'Architecture logicielle', 'Développement complet', 'Tests & Déploiement']
    },
    {
      icon: 'fa-book-open',
      title: 'Mémoires de Master',
      description: 'Guidance experte pour vos mémoires de Master Professionnel et Recherche avec méthodologies rigoureuses.',
      features: ['Problématique & Hypothèses', 'Revue de littérature', 'Méthodologie', 'Rédaction académique']
    },
    {
      icon: 'fa-chart-line',
      title: 'Projets Power BI',
      description: 'De la préparation des données jusqu\'au déploiement de dashboards interactifs et professionnels.',
      features: ['ETL & Nettoyage', 'Modélisation', 'Visualisations', 'Déploiement Cloud']
    },
    {
      icon: 'fa-robot',
      title: 'Machine Learning',
      description: 'Projets ML complets avec CRISP-DM : prétraitement, modélisation, optimisation et déploiement.',
      features: ['Data Preprocessing', 'Feature Engineering', 'Modèles ML/DL', 'MLOps & Déploiement']
    },
    {
      icon: 'fa-file-alt',
      title: 'Rapports LaTeX',
      description: 'Rédaction de rapports professionnels avec LaTeX suivant les normes académiques internationales.',
      features: ['Templates pro', 'Bibliographie', 'Figures & Tableaux', 'Mise en page parfaite']
    },
    {
      icon: 'fa-chalkboard-teacher',
      title: 'Présentations',
      description: 'Création de présentations impactantes pour vos soutenances avec design professionnel.',
      features: ['Design moderne', 'Animations', 'Storytelling', 'Coaching oral']
    }
  ];

  return (
    <section id="services" className="services">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Nos Services</span>
          <h2>Solutions Complètes pour Votre <span className="highlight">Réussite Académique</span></h2>
          <p>Un accompagnement personnalisé adapté à chaque étape de votre parcours</p>
        </div>
        <div className="services-grid">
          {services.map((service, index) => (
            <div key={index} className="service-card" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="service-icon">
                <i className={`fas ${service.icon}`}></i>
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature, i) => (
                  <li key={i}><i className="fas fa-check"></i>{feature}</li>
                ))}
              </ul>
              <a href="#contact" className="service-link">
                En savoir plus <i className="fas fa-arrow-right"></i>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Technologies Section
const Technologies = () => {
  const techCategories = [
    {
      title: 'Langages & Frameworks',
      icon: 'fa-code',
      items: [
        { name: 'Java', icon: 'fab fa-java' },
        { name: 'JavaScript', icon: 'fab fa-js-square' },
        { name: 'Python', icon: 'fab fa-python' },
        { name: '.NET/C#', icon: 'fab fa-microsoft' },
        { name: 'Angular', icon: 'fab fa-angular' },
        { name: 'React.js', icon: 'fab fa-react' },
        { name: 'Vue.js', icon: 'fab fa-vuejs' },
        { name: 'Spring Boot', icon: 'fas fa-leaf' },
        { name: 'Django', icon: 'fab fa-python' },
        { name: 'Express.js', icon: 'fab fa-node-js' },
        { name: 'ASP.NET Core', icon: 'fab fa-microsoft' }
      ]
    },
    {
      title: 'Mobile',
      icon: 'fa-mobile-alt',
      items: [
        { name: 'Android', icon: 'fab fa-android' },
        { name: 'Flutter', icon: 'fas fa-feather' },
        { name: 'React Native', icon: 'fab fa-react' }
      ]
    },
    {
      title: 'Bases de Données',
      icon: 'fa-database',
      items: [
        { name: 'Oracle', icon: 'fas fa-database' },
        { name: 'SQL Server', icon: 'fas fa-server' },
        { name: 'PostgreSQL', icon: 'fas fa-database' },
        { name: 'MongoDB', icon: 'fas fa-leaf' },
        { name: 'Hadoop', icon: 'fas fa-server' },
        { name: 'HBase', icon: 'fas fa-table' }
      ]
    },
    {
      title: 'DevOps & Cloud',
      icon: 'fa-cloud',
      items: [
        { name: 'Docker', icon: 'fab fa-docker' },
        { name: 'Kubernetes', icon: 'fas fa-dharmachakra' },
        { name: 'Jenkins', icon: 'fab fa-jenkins' },
        { name: 'GitHub Actions', icon: 'fab fa-github' },
        { name: 'Grafana', icon: 'fas fa-chart-area' },
        { name: 'Prometheus', icon: 'fas fa-fire' },
        { name: 'SonarQube', icon: 'fas fa-bug' }
      ]
    },
    {
      title: 'IA & Data Science',
      icon: 'fa-brain',
      items: [
        { name: 'NLP', icon: 'fas fa-language' },
        { name: 'Scikit-learn', icon: 'fas fa-cogs' },
        { name: 'TensorFlow', icon: 'fas fa-brain' },
        { name: 'Power BI', icon: 'fas fa-chart-pie' },
        { name: 'Tableau', icon: 'fas fa-chart-bar' }
      ]
    },
    {
      title: 'ERP & Autres',
      icon: 'fa-building',
      items: [
        { name: 'SAP', icon: 'fas fa-cubes' },
        { name: 'Salesforce', icon: 'fab fa-salesforce' },
        { name: 'Git', icon: 'fab fa-git-alt' },
        { name: 'REST APIs', icon: 'fas fa-plug' },
        { name: 'Microservices', icon: 'fas fa-cubes' }
      ]
    }
  ];

  return (
    <section id="technologies" className="technologies">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Technologies</span>
          <h2>Expertise <span className="highlight">Multi-Technologies</span></h2>
          <p>Maîtrise complète des technologies les plus demandées sur le marché</p>
        </div>
        <div className="tech-grid">
          {techCategories.map((category, index) => (
            <div key={index} className="tech-category" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="tech-category-header">
                <i className={`fas ${category.icon}`}></i>
                <h3>{category.title}</h3>
              </div>
              <div className="tech-items">
                {category.items.map((item, i) => (
                  <div key={i} className="tech-item">
                    <i className={item.icon}></i>
                    <span>{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Methodologies Section
const Methodologies = () => {
  const methodologies = [
    {
      title: 'Méthodologies Agile',
      subtitle: 'Pour le développement logiciel',
      icon: 'fa-sync-alt',
      color: '#4f46e5',
      steps: [
        { name: 'Sprint Planning', desc: 'Planification des itérations' },
        { name: 'Daily Scrum', desc: 'Suivi quotidien' },
        { name: 'Sprint Review', desc: 'Démonstration des résultats' },
        { name: 'Retrospective', desc: 'Amélioration continue' }
      ]
    },
    {
      title: 'CRISP-DM',
      subtitle: 'Pour le Machine Learning',
      icon: 'fa-project-diagram',
      color: '#059669',
      steps: [
        { name: 'Business Understanding', desc: 'Compréhension métier' },
        { name: 'Data Understanding', desc: 'Exploration des données' },
        { name: 'Data Preparation', desc: 'Préparation & nettoyage' },
        { name: 'Modeling', desc: 'Modélisation ML' },
        { name: 'Evaluation', desc: 'Évaluation des modèles' },
        { name: 'Deployment', desc: 'Mise en production' }
      ]
    }
  ];

  return (
    <section id="methodologies" className="methodologies">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Méthodologies</span>
          <h2>Approches <span className="highlight">Professionnelles</span></h2>
          <p>Des méthodologies éprouvées pour garantir la qualité de vos projets</p>
        </div>
        <div className="methodologies-grid">
          {methodologies.map((method, index) => (
            <div key={index} className="methodology-card">
              <div className="methodology-header" style={{ borderColor: method.color }}>
                <div className="methodology-icon" style={{ backgroundColor: method.color }}>
                  <i className={`fas ${method.icon}`}></i>
                </div>
                <div>
                  <h3>{method.title}</h3>
                  <span>{method.subtitle}</span>
                </div>
              </div>
              <div className="methodology-steps">
                {method.steps.map((step, i) => (
                  <div key={i} className="methodology-step">
                    <div className="step-number" style={{ backgroundColor: method.color }}>{i + 1}</div>
                    <div className="step-content">
                      <h4>{step.name}</h4>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Us Section
const WhyUs = () => {
  const reasons = [
    {
      icon: 'fa-user-graduate',
      title: 'Experts Qualifiés',
      description: 'Équipe de professionnels certifiés avec une expérience académique et industrielle solide.'
    },
    {
      icon: 'fa-clock',
      title: 'Respect des Délais',
      description: 'Livraison garantie dans les temps avec un suivi régulier de l\'avancement.'
    },
    {
      icon: 'fa-shield-alt',
      title: 'Confidentialité',
      description: 'Protection totale de vos données et travaux avec discrétion absolue.'
    },
    {
      icon: 'fa-comments',
      title: 'Support 24/7',
      description: 'Disponibilité permanente pour répondre à vos questions et préoccupations.'
    },
    {
      icon: 'fa-medal',
      title: 'Qualité Premium',
      description: 'Standards élevés respectant les normes académiques internationales.'
    },
    {
      icon: 'fa-hand-holding-usd',
      title: 'Prix Compétitifs',
      description: 'Tarifs adaptés aux étudiants avec un excellent rapport qualité-prix.'
    }
  ];

  return (
    <section id="pourquoi" className="why-us">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Pourquoi Nous</span>
          <h2>Votre <span className="highlight">Succès</span> est Notre Priorité</h2>
          <p>Ce qui nous distingue dans l'accompagnement académique</p>
        </div>
        <div className="why-us-grid">
          {reasons.map((reason, index) => (
            <div key={index} className="why-us-card" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="why-us-icon">
                <i className={`fas ${reason.icon}`}></i>
              </div>
              <h3>{reason.title}</h3>
              <p>{reason.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Specialties Section
const Specialties = () => {
  const specialties = [
    'Informatique', 'Génie Logiciel', 'Data Science', 'Intelligence Artificielle',
    'Cybersécurité', 'Réseaux', 'Systèmes Embarqués', 'IoT',
    'Gestion', 'Finance', 'Marketing Digital', 'Commerce International',
    'Génie Civil', 'Génie Mécanique', 'Génie Électrique', 'Automatisme',
    'Biotechnologie', 'Agronomie', 'Environnement', 'Énergies Renouvelables'
  ];

  return (
    <section className="specialties">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Spécialités</span>
          <h2>Toutes les <span className="highlight">Filières</span> Couvertes</h2>
          <p>Expertise dans plus de 50 domaines et spécialités</p>
        </div>
        <div className="specialties-wrapper">
          <div className="specialties-track">
            {[...specialties, ...specialties].map((specialty, index) => (
              <div key={index} className="specialty-tag">
                <i className="fas fa-graduation-cap"></i>
                {specialty}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:pfememoire.tn@gmail.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
      `Nom: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    )}`;
    window.location.href = mailtoLink;
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Contact</span>
          <h2>Lancez Votre <span className="highlight">Projet</span> Aujourd'hui</h2>
          <p>Contactez-nous pour une consultation gratuite</p>
        </div>
        <div className="contact-wrapper">
          <div className="contact-info">
            <div className="contact-info-header">
              <Logo />
              <h3>PFE & Master Coach</h3>
              <p>Votre réussite académique commence ici</p>
            </div>
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div>
                  <h4>Email</h4>
                  <a href="mailto:pfememoire.tn@gmail.com">pfememoire.tn@gmail.com</a>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">
                  <i className="fab fa-facebook"></i>
                </div>
                <div>
                  <h4>Facebook</h4>
                  <a href="https://www.facebook.com/profile.php?id=61586838344704" target="_blank" rel="noopener noreferrer">
                    PFE & Master Coach
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div>
                  <h4>Localisation</h4>
                  <span>Tunisie 🇹🇳</span>
                </div>
              </div>
            </div>
            <div className="social-links">
              <a href="https://www.facebook.com/profile.php?id=61586838344704" target="_blank" rel="noopener noreferrer" className="social-link facebook">
                <i className="fab fa-facebook-f"></i>
              </a>
              <a href="mailto:pfememoire.tn@gmail.com" className="social-link email">
                <i className="fas fa-envelope"></i>
              </a>
            </div>
          </div>
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Nom Complet</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Votre nom"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="votre@email.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="subject">Sujet</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                >
                  <option value="">Sélectionnez un service</option>
                  <option value="Projet PFE">Projet PFE</option>
                  <option value="Mémoire Master">Mémoire Master</option>
                  <option value="Projet Power BI">Projet Power BI</option>
                  <option value="Projet Machine Learning">Projet Machine Learning</option>
                  <option value="Rapport LaTeX">Rapport LaTeX</option>
                  <option value="Présentation">Présentation</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Décrivez votre projet..."
                  rows="5"
                ></textarea>
              </div>
              <button type="submit" className="btn btn-primary btn-block">
                <i className="fas fa-paper-plane"></i>
                Envoyer le Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-content">
        <div className="footer-brand">
          <Logo />
          <h3>PFE & Master Coach</h3>
          <p>Accompagnement académique de qualité pour vos projets PFE et mémoires de Master.</p>
        </div>
        <div className="footer-links">
          <h4>Navigation</h4>
          <a href="#accueil">Accueil</a>
          <a href="#services">Services</a>
          <a href="#technologies">Technologies</a>
          <a href="#contact">Contact</a>
        </div>
        <div className="footer-links">
          <h4>Services</h4>
          <a href="#services">Projets PFE</a>
          <a href="#services">Mémoires Master</a>
          <a href="#services">Power BI</a>
          <a href="#services">Machine Learning</a>
        </div>
        <div className="footer-contact">
          <h4>Contact</h4>
          <a href="mailto:pfememoire.tn@gmail.com">
            <i className="fas fa-envelope"></i> pfememoire.tn@gmail.com
          </a>
          <a href="https://www.facebook.com/profile.php?id=61586838344704" target="_blank" rel="noopener noreferrer">
            <i className="fab fa-facebook"></i> Facebook
          </a>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; 2026 PFE & Master Coach. Tous droits réservés.</p>
        <p>Fait avec <i className="fas fa-heart"></i> en Tunisie 🇹🇳</p>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  const [activeSection, setActiveSection] = useState('accueil');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['accueil', 'services', 'technologies', 'methodologies', 'pourquoi', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="App">
      <Navbar activeSection={activeSection} />
      <Hero />
      <Services />
      <Technologies />
      <Methodologies />
      <WhyUs />
      <Specialties />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
